import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Save, Bell, Search, BarChart3, History, Globe, 
  AlertCircle, CheckCircle, Mail, Settings2, Code,
  ExternalLink, Info, Trash2
} from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Notification settings
  const [notifications, setNotifications] = useState({
    notification_emails: [],
    notify_on_subscribe: true,
    notify_on_contact: true,
    site_name: 'teamWERK Erolzheim',
    admin_email: '',
    newsletter_sender_name: 'teamWERK Erolzheim',
    newsletter_sender_email: ''
  });
  
  // SEO settings
  const [seo, setSeo] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image: '',
    robots: 'index, follow'
  });
  
  // Analytics settings
  const [analytics, setAnalytics] = useState({
    google_analytics_id: '',
    google_tag_manager_id: '',
    plausible_domain: '',
    matomo_url: '',
    matomo_site_id: '',
    custom_head_scripts: '',
    custom_body_scripts: ''
  });
  
  // Change history
  const [history, setHistory] = useState([]);
  
  // New email input
  const [newEmail, setNewEmail] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    setLoading(true);
    try {
      const [notifRes, seoRes, analyticsRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/settings`, { headers }),
        axios.get(`${BACKEND_URL}/api/seo`, { headers }),
        axios.get(`${BACKEND_URL}/api/analytics`, { headers }),
        axios.get(`${BACKEND_URL}/api/history?limit=30`, { headers })
      ]);
      
      setNotifications(notifRes.data);
      setSeo(seoRes.data);
      setAnalytics(analyticsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    }
    setLoading(false);
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/settings`, notifications, { headers });
      toast.success('Benachrichtigungs-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const saveSeo = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/seo`, seo, { headers });
      toast.success('SEO-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const saveAnalytics = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/analytics`, analytics, { headers });
      toast.success('Analytics-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const addEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Bitte gültige E-Mail eingeben');
      return;
    }
    if (notifications.notification_emails.includes(newEmail)) {
      toast.error('E-Mail bereits vorhanden');
      return;
    }
    setNotifications({
      ...notifications,
      notification_emails: [...notifications.notification_emails, newEmail]
    });
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setNotifications({
      ...notifications,
      notification_emails: notifications.notification_emails.filter(e => e !== email)
    });
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4 text-gray-500">Einstellungen laden...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Einstellungen</h1>
        <p className="text-gray-500 mt-2">System- und CMS-Konfiguration</p>
      </div>

      <Tabs defaultValue="notifications" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 h-auto">
          <TabsTrigger value="notifications" className="gap-2 py-3">
            <Bell size={16} /> Benachrichtigungen
          </TabsTrigger>
          <TabsTrigger value="seo" className="gap-2 py-3">
            <Search size={16} /> SEO
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2 py-3">
            <BarChart3 size={16} /> Analytics
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2 py-3">
            <History size={16} /> Änderungen
          </TabsTrigger>
        </TabsList>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            <div>
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" /> Benachrichtigungs-Empfänger
              </h3>
              
              <div className="flex gap-2 mb-4">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="email@example.com"
                  onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                />
                <Button onClick={addEmail} variant="outline">Hinzufügen</Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {notifications.notification_emails?.map((email, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">
                    <span className="text-sm">{email}</span>
                    <button
                      onClick={() => removeEmail(email)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {(!notifications.notification_emails || notifications.notification_emails.length === 0) && (
                  <p className="text-gray-400 text-sm">Keine E-Mail-Adressen konfiguriert</p>
                )}
              </div>
            </div>

            <div className="border-t pt-6">
              <h4 className="font-medium mb-4">Benachrichtigen bei:</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.notify_on_subscribe}
                    onChange={(e) => setNotifications({...notifications, notify_on_subscribe: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300"
                  />
                  <span>Neuer Newsletter-Abonnent</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifications.notify_on_contact}
                    onChange={(e) => setNotifications({...notifications, notify_on_contact: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300"
                  />
                  <span>Neue Kontaktanfrage</span>
                </label>
              </div>
            </div>

            <div className="border-t pt-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Newsletter Absender-Name</label>
                <Input
                  value={notifications.newsletter_sender_name}
                  onChange={(e) => setNotifications({...notifications, newsletter_sender_name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Newsletter Absender-Email</label>
                <Input
                  type="email"
                  value={notifications.newsletter_sender_email}
                  onChange={(e) => setNotifications({...notifications, newsletter_sender_email: e.target.value})}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={saveNotifications} disabled={saving} className="bg-black hover:bg-gray-800">
                <Save size={16} className="mr-2" /> Speichern
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">SEO Meta-Tags</p>
                <p>Diese Einstellungen werden auf allen Seiten verwendet und verbessern die Sichtbarkeit in Suchmaschinen.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Meta-Titel</label>
              <Input
                value={seo.meta_title}
                onChange={(e) => setSeo({...seo, meta_title: e.target.value})}
                placeholder="teamWERK Erolzheim - Coworking Space"
              />
              <p className="text-xs text-gray-400 mt-1">Max. 60 Zeichen empfohlen ({seo.meta_title?.length || 0}/60)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Meta-Beschreibung</label>
              <textarea
                value={seo.meta_description}
                onChange={(e) => setSeo({...seo, meta_description: e.target.value})}
                className="w-full p-3 border border-gray-200 rounded-lg resize-none h-24"
                placeholder="Moderner Coworking Space in Erolzheim..."
              />
              <p className="text-xs text-gray-400 mt-1">Max. 160 Zeichen empfohlen ({seo.meta_description?.length || 0}/160)</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Keywords</label>
              <Input
                value={seo.meta_keywords}
                onChange={(e) => setSeo({...seo, meta_keywords: e.target.value})}
                placeholder="coworking, büro, erolzheim, tagungsraum"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Open Graph Bild (für Social Media)</label>
              <Input
                value={seo.og_image}
                onChange={(e) => setSeo({...seo, og_image: e.target.value})}
                placeholder="/assets/og-image.jpg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Robots</label>
              <select
                value={seo.robots}
                onChange={(e) => setSeo({...seo, robots: e.target.value})}
                className="w-full p-3 border border-gray-200 rounded-lg"
              >
                <option value="index, follow">Index, Follow (Empfohlen)</option>
                <option value="index, nofollow">Index, Nofollow</option>
                <option value="noindex, follow">Noindex, Follow</option>
                <option value="noindex, nofollow">Noindex, Nofollow</option>
              </select>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={saveSeo} disabled={saving} className="bg-black hover:bg-gray-800">
                <Save size={16} className="mr-2" /> Speichern
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
            <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Analytics-Integration</p>
                <p>Verbinden Sie Ihre Analytics-Plattform um Besucherstatistiken zu tracken. Sie können auch ein Dashboard per iFrame einbetten.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Google Analytics ID
                </label>
                <Input
                  value={analytics.google_analytics_id}
                  onChange={(e) => setAnalytics({...analytics, google_analytics_id: e.target.value})}
                  placeholder="G-XXXXXXXXXX oder UA-XXXXXXXX-X"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Google Tag Manager ID</label>
                <Input
                  value={analytics.google_tag_manager_id}
                  onChange={(e) => setAnalytics({...analytics, google_tag_manager_id: e.target.value})}
                  placeholder="GTM-XXXXXXX"
                />
              </div>
            </div>

            <div className="border-t pt-6">
              <h4 className="font-medium mb-4">Alternative Analytics (DSGVO-freundlich)</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Plausible Domain</label>
                  <Input
                    value={analytics.plausible_domain}
                    onChange={(e) => setAnalytics({...analytics, plausible_domain: e.target.value})}
                    placeholder="teamwerk-erolzheim.de"
                  />
                </div>
                <div className="col-span-2 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Matomo URL</label>
                    <Input
                      value={analytics.matomo_url}
                      onChange={(e) => setAnalytics({...analytics, matomo_url: e.target.value})}
                      placeholder="https://analytics.example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Matomo Site ID</label>
                    <Input
                      value={analytics.matomo_site_id}
                      onChange={(e) => setAnalytics({...analytics, matomo_site_id: e.target.value})}
                      placeholder="1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h4 className="font-medium mb-4 flex items-center gap-2">
                <Code className="w-4 h-4" /> Custom Scripts
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Head Scripts (vor &lt;/head&gt;)</label>
                  <textarea
                    value={analytics.custom_head_scripts}
                    onChange={(e) => setAnalytics({...analytics, custom_head_scripts: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg font-mono text-sm h-24 resize-none"
                    placeholder="<!-- Custom tracking code -->"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Body Scripts (vor &lt;/body&gt;)</label>
                  <textarea
                    value={analytics.custom_body_scripts}
                    onChange={(e) => setAnalytics({...analytics, custom_body_scripts: e.target.value})}
                    className="w-full p-3 border border-gray-200 rounded-lg font-mono text-sm h-24 resize-none"
                    placeholder="<!-- Custom scripts -->"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={saveAnalytics} disabled={saving} className="bg-black hover:bg-gray-800">
                <Save size={16} className="mr-2" /> Speichern
              </Button>
            </div>
          </div>

          {/* Analytics Dashboard iFrame Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> Analytics Dashboard einbinden
            </h3>
            
            <div className="prose prose-sm max-w-none text-gray-600">
              <p>Sie können ein externes Analytics-Dashboard als iFrame in Ihr CMS einbinden:</p>
              
              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <h4 className="font-bold text-black mb-2">Option 1: Plausible Analytics (empfohlen)</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Erstellen Sie ein Konto bei <a href="https://plausible.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">plausible.io</a></li>
                  <li>Fügen Sie Ihre Domain hinzu</li>
                  <li>Aktivieren Sie "Public Dashboard" in den Site-Einstellungen</li>
                  <li>Kopieren Sie die öffentliche Dashboard-URL</li>
                </ol>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <h4 className="font-bold text-black mb-2">Option 2: Matomo (Self-hosted)</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Installieren Sie Matomo auf Ihrem Server</li>
                  <li>Erstellen Sie einen API-Token mit Leserechten</li>
                  <li>Geben Sie Matomo URL und Site ID oben ein</li>
                  <li>Das Dashboard wird automatisch eingebettet</li>
                </ol>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mt-4">
                <h4 className="font-bold text-black mb-2">Option 3: Google Analytics</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Gehen Sie zu <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">analytics.google.com</a></li>
                  <li>Erstellen Sie einen "Shareable Report Link"</li>
                  <li>Für Echtzeit-Einbettung: Looker Studio verwenden</li>
                </ol>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-bold">Änderungsprotokoll</h3>
              <p className="text-sm text-gray-500">Letzte Änderungen im CMS</p>
            </div>
            
            {history.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Noch keine Änderungen protokolliert</p>
              </div>
            ) : (
              <div className="divide-y">
                {history.map((entry, idx) => (
                  <div key={idx} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${
                          entry.action === 'create' ? 'bg-green-100 text-green-600' :
                          entry.action === 'update' ? 'bg-blue-100 text-blue-600' :
                          entry.action === 'delete' ? 'bg-red-100 text-red-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {entry.action === 'create' && <CheckCircle size={16} />}
                          {entry.action === 'update' && <Settings2 size={16} />}
                          {entry.action === 'delete' && <Trash2 size={16} />}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {entry.action === 'create' && 'Erstellt: '}
                            {entry.action === 'update' && 'Geändert: '}
                            {entry.action === 'delete' && 'Gelöscht: '}
                            {entry.entity_type}
                          </p>
                          <p className="text-sm text-gray-500">{entry.user}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(entry.timestamp)}</span>
                    </div>
                    {entry.details && Object.keys(entry.details).length > 0 && (
                      <div className="mt-2 ml-11 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                        {JSON.stringify(entry.details, null, 2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
